import React, { useState, useEffect } from 'react';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { transformToHierarchicalData, transformToYearHierarchicalData, initializeCountryRegions } from '../../moduleServices/trips_sanitize';
import Spinner from '../../moduleComponents/SpinnerLoad';
import CountryBadge from '../../moduleComponents/CountryBadge';

const CardDynamicTable = ({ trips, isTripsLoading, viewMode = 'region' }) => {
  const [data, setData] = useState([]);
  const [expandedRegions, setExpandedRegions] = useState({});
  const [expandedCountries, setExpandedCountries] = useState({});
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedTrips, setExpandedTrips] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  // ##### Initialize and transform data based on viewMode
  useEffect(() => {
    if (!trips || trips.length === 0) {
      setData([]);
      return;
    }

    const initializeData = async () => {
      try {
        setIsProcessing(true);
        
        let hierarchicalData;
        
        if (viewMode === 'year') {
          // Transform by year (no need for country regions)
          hierarchicalData = transformToYearHierarchicalData(trips);
        } else {
          // Transform by region (needs country regions)
          await initializeCountryRegions();
          hierarchicalData = transformToHierarchicalData(trips);
        }
        
        setData(hierarchicalData);
      } catch (error) {
        console.error('Error processing travel data:', error);
        setData([]);
      } finally {
        setIsProcessing(false);
      }
    };
    
    initializeData();
  }, [trips, viewMode]);

  // ##### Toggle region expansion
  const toggleRegion = (region) => {
    setExpandedRegions(prev => ({
      ...prev,
      [region]: !prev[region]
    }));
  };

  // ##### Toggle country expansion
  const toggleCountry = (region, country) => {
    const key = `${region}_${country}`;
    setExpandedCountries(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // ##### Toggle year expansion
  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  // ##### Toggle trip expansion
  const toggleTrip = (year, tripId) => {
    const key = `${year}_${tripId}`;
    setExpandedTrips(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // ##### Get visible rows based on expansion state
  const getVisibleRows = () => {
    const visibleRows = [];
    
    if (viewMode === 'year') {
      // Year mode: year -> trip -> place
      data.forEach(row => {
        const level = row.level;
        
        if (level === 'year') {
          visibleRows.push(row);
          
          if (expandedYears[row.year]) {
            const tripsInYear = data.filter(r => r.level === 'trip' && r.year === row.year);
            tripsInYear.forEach(trip => {
              visibleRows.push(trip);
              
              if (expandedTrips[`${row.year}_${trip.tripId}`]) {
                const placesInTrip = data.filter(r => r.level === 'place' && r.year === row.year && r.tripId === trip.tripId);
                visibleRows.push(...placesInTrip);
              }
            });
          }
        }
      });
    } else {
      // Region mode: region -> country -> trips
      data.forEach(row => {
        const level = row.level;
        
        if (level === 'region') {
          visibleRows.push(row);
          
          if (expandedRegions[row.region]) {
            const countriesInRegion = data.filter(r => r.level === 'country' && r.region === row.region);
            countriesInRegion.forEach(country => {
              visibleRows.push(country);
              
              if (expandedCountries[`${row.region}_${country.country}`]) {
                const tripsInCountry = data.filter(r => r.level === 'trip' && r.region === row.region && r.country === country.country);
                visibleRows.push(...tripsInCountry);
              }
            });
          }
        }
      });
    }
    
    return visibleRows;
  };

  const columns = [
    {
      id: 'drilldown',
      accessorKey: viewMode === 'year' ? 'year' : 'region',
      cell: ({ row }) => {
        const level = row.level;
        
        // Render toggle button for grouped rows
        let toggleButton = null;
        if (viewMode === 'year') {
          if (level === 'year') {
            const isExpanded = expandedYears[row.year];
            toggleButton = (
              <button
                onClick={() => toggleYear(row.year)}
                className="rounded hover:bg-gray-100 p-0.5"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                )}
              </button>
            );
          } else if (level === 'trip') {
            const key = `${row.year}_${row.tripId}`;
            const isExpanded = expandedTrips[key];
            toggleButton = (
              <button
                onClick={() => toggleTrip(row.year, row.tripId)}
                className="rounded hover:bg-gray-100 p-0.5"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                )}
              </button>
            );
          }
        } else {
          if (level === 'region') {
            const isExpanded = expandedRegions[row.region];
            toggleButton = (
              <button
                onClick={() => toggleRegion(row.region)}
                className="rounded hover:bg-gray-100 p-0.5"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                )}
              </button>
            );
          } else if (level === 'country') {
            const key = `${row.region}_${row.country}`;
            const isExpanded = expandedCountries[key];
            toggleButton = (
              <button
                onClick={() => toggleCountry(row.region, row.country)}
                className="rounded hover:bg-gray-100 p-0.5"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                )}
              </button>
            );
          }
        }

        // Render content based on level and viewMode
        let content;
        if (viewMode === 'year') {
          if (level === 'year') {
            // Year level: show only year (no parentheses)
            content = (
              <span className="font-semibold text-gray-800">
                {row.year}
              </span>
            );
          } else if (level === 'trip') {
            // Trip level: show trip name and date
            const startDate = row.startDate;
            const formattedDate = startDate ? new Date(startDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }) : 'N/A';
            
            content = (
              <div className="flex flex-col">
                <span className="font-medium text-gray-700">{row.tripName || 'Unnamed Trip'}</span>
                <span className="text-xs text-gray-500">{formattedDate}</span>
              </div>
            );
          } else if (level === 'place') {
            // Place level: show place name, arrival and departure dates (no trip name in year mode - it's redundant)
            const arrivalDate = row.placeArrivalDate;
            const departureDate = row.placeDepartureDate;
            
            const formatDate = (date) => {
              if (!date) return null;
              return new Date(date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
            };
            
            const formattedArrival = formatDate(arrivalDate);
            const formattedDeparture = formatDate(departureDate);
            
            content = (
              <div className="flex flex-col">
                <span className={`text-gray-700 ${viewMode === 'year' ? 'font-normal' : 'font-medium'}`}>{row.placeName || 'Unnamed Place'}</span>
                {formattedArrival && formattedDeparture && (
                  <span className="text-xs text-gray-500">
                    {formattedArrival} - {formattedDeparture}
                  </span>
                )}
                {formattedArrival && !formattedDeparture && (
                  <span className="text-xs text-gray-500">From {formattedArrival}</span>
                )}
                {!formattedArrival && formattedDeparture && (
                  <span className="text-xs text-gray-500">Until {formattedDeparture}</span>
                )}
                {!formattedArrival && !formattedDeparture && (
                  <span className="text-xs text-gray-500">No dates</span>
                )}
              </div>
            );
          }
        } else {
          if (level === 'region') {
            content = (
              <span className="font-semibold text-gray-800">
                {row.region}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({row.uniqueCountries})
                </span>
              </span>
            );
          } else if (level === 'country') {
            content = (
              <div className="flex items-center gap-2">
                {row.countryCode && <CountryBadge code={row.countryCode} size={18} />}
                <span className="font-medium text-gray-700">{row.country}</span>
              </div>
            );
          } else if (level === 'trip') {
            const startDate = row.startDate;
            const formattedDate = startDate ? new Date(startDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }) : 'N/A';
            
            content = (
              <div className="flex flex-col">
                <span className="font-medium text-gray-700">{row.placeName || 'Unnamed Place'}</span>
                <span className="text-xs text-gray-500">{formattedDate}</span>
                {row.tripName && (
                  <span className="text-xs text-gray-400">{row.tripName}</span>
                )}
              </div>
            );
          }
        }

        return (
          <div className="flex items-center gap-2">
            {toggleButton}
            {content}
          </div>
        );
      }
    },
    {
      id: 'tripCount',
      accessorKey: 'tripCount',
      cell: ({ row }) => {
        const level = row.level;
        const count = row.tripCount;
        
        // Only show count for grouped rows
        if (viewMode === 'year') {
          // Year mode: only show for year level
          if (level !== 'year') return null;
        } else {
          // Region mode: show for region and country levels
          if (level === 'trip') return null;
        }
        
        return (
          <span className="text-gray-600">
            {count} <span className="text-gray-500 text-sm">
              {count === 1 ? 'trip' : 'trips'}
            </span>
          </span>
        );
      }
    }
  ];

  if (isTripsLoading || isProcessing) {
    return (
      <div className="flex items-center justify-center h-48">
        <Spinner />
      </div>
    );
  }

  // Show message if no data
  if (!trips || trips.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-500">
        <div className="text-center">
          <p className="text-sm">No travel data available</p>
          <p className="text-xs text-gray-400">Start by adding some trips and places</p>
        </div>
      </div>
    );
  }

  const visibleRows = getVisibleRows();

  return (
    <div className="px-2">
      <table className="min-w-full">
        <tbody>
          {visibleRows.map(row => {
            const level = row.level;
            
            // Calculate padding based on level and viewMode
            let paddingLeft = '1rem';
            if (viewMode === 'year') {
              // Year mode: year (0) -> trip (1) -> place (2) with extra indent for place
              if (level === 'place') {
                paddingLeft = `${(2 * 1.5 + 1) + 0.75}rem`; // Extra 0.75rem indent for place
              } else {
                paddingLeft = `${(level === 'year' ? 0 : 1) * 1.5 + 1}rem`;
              }
            } else {
              // Region mode: region (0) -> country (1) -> trip (2)
              paddingLeft = `${(level === 'region' ? 0 : level === 'country' ? 1 : 2) * 1.5 + 1}rem`;
            }
            
            // Determine row styling - reuse same styles for both modes
            let rowClassName = 'hover:bg-gray-50';
            if (viewMode === 'year') {
              // Year mode: reuse region/country/trip styles
              if (level === 'year') {
                rowClassName = 'bg-gradient-to-r from-blue-50/70 to-blue-100/30 hover:from-blue-50';
              } else if (level === 'trip') {
                rowClassName = 'bg-gradient-to-r from-gray-50/50 to-gray-100/30 hover:from-gray-50';
              }
            } else {
              // Region mode: existing styles
              if (level === 'region') {
                rowClassName = 'bg-gradient-to-r from-blue-50/70 to-blue-100/30 hover:from-blue-50';
              } else if (level === 'country') {
                rowClassName = 'bg-gradient-to-r from-gray-50/50 to-gray-100/30 hover:from-gray-50';
              }
            }
            
            return (
              <tr
                key={row.id}
                className={rowClassName}
              >
                {columns.map(column => (
                  <td
                    key={column.id}
                    className={`py-2 text-sm ${column.id === 'tripCount' ? 'text-right' : ''}`}
                    style={{
                      paddingLeft: column.id === 'tripCount' ? '0' : paddingLeft,
                      paddingRight: column.id === 'tripCount' ? '0.5rem' : undefined,
                    }}
                  >
                    {column.cell({ row })}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CardDynamicTable;